import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { fetchCurrentUser, fetchCoreLogicAuthToken, endSession, logoutWithRefreshToken } from "../services/auth.service";
import { AlertColor } from "@mui/material";

interface DataValue {
  login: any;
  logout: any;
  setLogin: any;

  snackbar: any;
  setSnackbar: any;
}
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataContext = createContext<DataValue | undefined>(undefined);

const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message?: string;
    alert?: AlertColor;
  }>({
    open: false,
    alert: "error",
    message: "",
  });
  const [login, setLogin] = useState({ isLoggedIn: undefined, data: null });

  useEffect(() => {
    const auth = async () => {
      const resp: any = await fetchCurrentUser(localStorage.getItem("token") || "");
      resp.token && localStorage.setItem("token", resp.token);
      setLogin({ isLoggedIn: !resp.hasError, data: resp.data });
    };
    localStorage.getItem("token")
      ? auth()
      : setLogin({ isLoggedIn: false, data: null });
  }, []);

  const getAccessToken = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const expiryTime = localStorage.getItem("expiryTime");

    if (accessToken && expiryTime) {
      const expiryTimestamp = parseInt(expiryTime, 10);
      if (Date.now() < expiryTimestamp) {
        // Token is still valid
        return;
      }
    }

    try {
      const resp: any = await fetchCoreLogicAuthToken();
      const { access_token, expires_in } = resp;

      // Calculate expiry time and store in local storage
      const expiryTimestamp = Date.now() + expires_in * 1000; // Convert to milliseconds
      localStorage.setItem("accessToken", access_token);
      localStorage.setItem("expiryTime", expiryTimestamp.toString());
    } catch (error) {
      console.error("Failed to fetch access token:", error);
    }
  };

  const refreshToken = async () => {
    try {
      // await getAccessToken();
    } catch (error) {
      console.error("Failed to refresh access token:", error);
      // Handle token refresh failure, possibly log user out
    }
  };

  useEffect(() => {
    // getAccessToken();

    // Set interval to check token expiry and refresh if needed
    const checkTokenInterval = setInterval(() => {
      const expiryTime = localStorage.getItem("expiryTime");
      if (expiryTime) {
        const expiryTimestamp = parseInt(expiryTime, 10);
        if (Date.now() >= expiryTimestamp) {
          refreshToken();
        }
      }
    }, 60000); // Check every minute

    // Cleanup function to clear interval
    return () => clearInterval(checkTokenInterval);
  }, []);

  const logout = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    await Promise.allSettled([
      endSession({ endReason: 'logout' }),
      refreshToken ? logoutWithRefreshToken(refreshToken) : Promise.resolve(),
    ]);
    setLogin({ isLoggedIn: false, data: null });
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // sessionStorage holds only per-tab UI convenience state (last-viewed client/contact,
    // active tab, etc. — see useSessionState.ts) — clear it so it can't leak into a
    // different account logging in on the same tab.
    sessionStorage.clear();
  };

  return (
    <DataContext.Provider
      value={{ login, setLogin, logout, snackbar, setSnackbar }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
